import React, { useEffect, useState, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import {
  fetchProductSizeWeights,
  updateProductSizeWeight,
  bulkUpdateProductSizeWeights,
} from "../../actions/product";
import FullPageSpinner from "../layout/FullPageSpinner";
import {
  Upload,
  Download,
  Search,
  Save,
  CheckCircle,
  AlertCircle,
  Scale,
  RefreshCw,
  FileSpreadsheet,
  Layers,
} from "lucide-react";

const SizeWeightManagement = () => {
  const [sizeWeights, setSizeWeights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [weightFilter, setWeightFilter] = useState("all"); // 'all', 'configured', 'missing'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  // Bulk Upload Modal State
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState([]);
  const [uploadApplying, setUploadApplying] = useState(false);
  const fileInputRef = useRef(null);

  // Editable rows state: { [rowKey]: { weight: number|string, uom: string, remarks: string } }
  const [editedRows, setEditedRows] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchProductSizeWeights();
      setSizeWeights(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error("Failed to load size weights");
    } finally {
      setLoading(false);
    }
  };

  // Categories for filter
  const categories = useMemo(() => {
    const cats = new Set();
    sizeWeights.forEach((item) => {
      if (item.category || item.group) {
        cats.add(item.category || item.group);
      }
    });
    return Array.from(cats).sort();
  }, [sizeWeights]);

  // Filtered & Searched Data
  const filteredData = useMemo(() => {
    return sizeWeights.filter((item) => {
      const pName = (item.productName || item.displayName || "").toLowerCase();
      const group = (item.group || item.category || "").toLowerCase();
      const size = (item.size || "").toLowerCase();
      const search = searchTerm.toLowerCase().trim();

      const matchesSearch =
        !search ||
        pName.includes(search) ||
        group.includes(search) ||
        size.includes(search);

      const matchesCategory =
        !selectedCategory || (item.group || item.category) === selectedCategory;

      const baseWeight = Number(item.weight) || 0;

      const matchesWeightFilter =
        weightFilter === "all" ||
        (weightFilter === "configured" && baseWeight > 0) ||
        (weightFilter === "missing" && baseWeight === 0);

      return matchesSearch && matchesCategory && matchesWeightFilter;
    });
  }, [sizeWeights, searchTerm, selectedCategory, weightFilter]);

  // Statistics
  const stats = useMemo(() => {
    const total = sizeWeights.length;
    const configured = sizeWeights.filter((item) => Number(item.weight) > 0).length;
    const missing = total - configured;
    return { total, configured, missing };
  }, [sizeWeights]);

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage, itemsPerPage]);

  const handleRowChange = (productId, size, field, value) => {
    let cleanVal = value;
    if (field === "weight") {
      cleanVal = value.replace(/,/g, ".");
      // Allow empty string or digits with optional single decimal point
      if (cleanVal !== "" && !/^\d*\.?\d*$/.test(cleanVal)) {
        return;
      }
    }
    const key = `${productId}_${size}`;
    setEditedRows((prev) => {
      const existing = prev[key] || {};
      return {
        ...prev,
        [key]: {
          ...existing,
          [field]: cleanVal,
        },
      };
    });
  };

  const handleSaveRow = async (item) => {
    const key = `${item.productId}_${item.size}`;
    const edits = editedRows[key] || {};
    const rawWeight = edits.weight !== undefined ? edits.weight : item.weight;
    const weightVal =
      rawWeight === "" || rawWeight === null || rawWeight === undefined
        ? 0
        : parseFloat(rawWeight);
    const uomVal = edits.uom !== undefined ? edits.uom : item.uom || "Num";
    const remarksVal =
      edits.remarks !== undefined ? edits.remarks : item.remarks || "";

    if (isNaN(weightVal) || weightVal < 0) {
      toast.error("Please enter a valid non-negative weight");
      return;
    }

    setSavingId(key);
    try {
      await updateProductSizeWeight({
        productId: item.productId,
        size: item.size,
        weight: weightVal,
        uom: uomVal,
        remarks: remarksVal,
      });

      // Update local state
      setSizeWeights((prev) =>
        prev.map((r) =>
          r.productId === item.productId && r.size === item.size
            ? { ...r, weight: weightVal, uom: uomVal, remarks: remarksVal }
            : r
        )
      );

      // Clear edit state for this row
      setEditedRows((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (error) {
      console.error(error);
    } finally {
      setSavingId(null);
    }
  };

  // Bulk save all unsaved in-line row edits
  const [savingAll, setSavingAll] = useState(false);
  const handleSaveAllEdited = async () => {
    const editKeys = Object.keys(editedRows);
    if (editKeys.length === 0) {
      toast.info("No unsaved changes to apply");
      return;
    }

    setSavingAll(true);
    try {
      const payload = editKeys.map((key) => {
        const [productId, size] = key.split("_");
        const edits = editedRows[key] || {};
        const item = sizeWeights.find(
          (sw) => String(sw.productId) === String(productId) && sw.size === size
        );
        const rawWeight = edits.weight !== undefined ? edits.weight : item?.weight;
        const weightVal =
          rawWeight === "" || rawWeight === null || rawWeight === undefined
            ? 0
            : parseFloat(rawWeight);

        return {
          productId: Number(productId),
          size,
          weight: isNaN(weightVal) ? 0 : weightVal,
          uom: edits.uom !== undefined ? edits.uom : item?.uom || "Num",
          remarks: edits.remarks !== undefined ? edits.remarks : item?.remarks || "",
        };
      });

      const res = await bulkUpdateProductSizeWeights(payload);
      if (res?.success || res?.successCount > 0) {
        setEditedRows({});
        loadData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSavingAll(false);
    }
  };

  // Export to Excel in the specified format:
  // Product ID | Group | Product | Size | UOM | Unit Weight (KG) | Remarks
  const handleExportExcel = () => {
    if (sizeWeights.length === 0) {
      toast.warn("No data available to export");
      return;
    }

    const exportRows = filteredData.map((item) => {
      const key = `${item.productId}_${item.size}`;
      const edits = editedRows[key] || {};
      const weight =
        edits.weight !== undefined ? Number(edits.weight) : Number(item.weight) || 0;
      const uom = edits.uom !== undefined ? edits.uom : item.uom || "Num";
      const remarks =
        edits.remarks !== undefined ? edits.remarks : item.remarks || "";

      return {
        "Product ID": item.productId,
        Group: item.group || item.category || "",
        Product: item.productName || item.displayName || "",
        Size: item.size || "",
        UOM: uom,
        "Unit Weight (KG)": Number(weight.toFixed(3)),
        Remarks: remarks,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);

    // Format column widths
    worksheet["!cols"] = [
      { wch: 12 }, // Product ID
      { wch: 22 }, // Group
      { wch: 35 }, // Product
      { wch: 12 }, // Size
      { wch: 10 }, // UOM
      { wch: 18 }, // Unit Weight (KG)
      { wch: 25 }, // Remarks
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Size Weights");

    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Size_Weights_Config_${timestamp}.xlsx`);
    toast.success("Exported size weights to Excel successfully!");
  };

  // Download Sample Template
  const handleDownloadSample = () => {
    const sampleRows = [
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P3",
        UOM: "Num",
        "Unit Weight (KG)": 0.080,
        Remarks: "Nur to Ukg",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P4",
        UOM: "Num",
        "Unit Weight (KG)": 0.090,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P5",
        UOM: "Num",
        "Unit Weight (KG)": 0.100,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P6",
        UOM: "Num",
        "Unit Weight (KG)": 0.110,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P8",
        UOM: "Num",
        "Unit Weight (KG)": 0.120,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P10",
        UOM: "Num",
        "Unit Weight (KG)": 0.140,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P12",
        UOM: "Num",
        "Unit Weight (KG)": 0.150,
        Remarks: "",
      },
      {
        Group: "Boys & Girls",
        Product: "# TGS PP Uni CC Tee - White",
        Size: "P14",
        UOM: "Num",
        "Unit Weight (KG)": 0.160,
        Remarks: "",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(sampleRows);
    worksheet["!cols"] = [
      { wch: 20 },
      { wch: 35 },
      { wch: 10 },
      { wch: 10 },
      { wch: 18 },
      { wch: 20 },
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");
    XLSX.writeFile(workbook, "Size_Weights_Sample_Template.xlsx");
  };

  // Parse Uploaded File
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const buffer = evt.target.result;
        const wb = XLSX.read(buffer, { type: "array" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

        if (!jsonData || jsonData.length < 2) {
          toast.error("The uploaded file is empty or missing data rows");
          return;
        }

        // Find the header row dynamically (within top 10 rows)
        let headerRowIdx = -1;
        let headerMap = {};

        for (let r = 0; r < Math.min(jsonData.length, 10); r++) {
          const row = jsonData[r] || [];
          const candidateMap = {};

          row.forEach((cell, idx) => {
            const str = String(cell || "").trim();
            const hl = str.toLowerCase().replace(/[^a-z0-9]/g, "");
            if (!hl) return;

            // 1. Product ID
            if (hl === "productid" || hl === "pid" || hl === "id" || hl === "itemid" || hl === "itemcode" || hl === "productcode") {
              candidateMap.productId = idx;
            }
            // 2. Weight / Wt / KG (Check BEFORE UOM / Unit)
            else if (
              hl.includes("weight") ||
              hl.includes("wt") ||
              hl.includes("unitweight") ||
              hl.includes("unitwt") ||
              hl.includes("netwt") ||
              hl.includes("grosswt") ||
              hl === "kg" ||
              hl === "weightkg" ||
              hl === "unitkg" ||
              hl === "gm" ||
              hl === "gms" ||
              hl === "grams"
            ) {
              candidateMap.weight = idx;
            }
            // 3. Product Name
            else if (hl.includes("product") || hl.includes("item") || hl.includes("itemname") || hl.includes("description") || hl.includes("particular")) {
              candidateMap.product = idx;
            }
            // 4. Size
            else if (hl.includes("size")) {
              candidateMap.size = idx;
            }
            // 5. Group / Category
            else if (hl.includes("group") || hl.includes("category") || hl.includes("class") || hl.includes("gender")) {
              candidateMap.group = idx;
            }
            // 6. UOM / Unit of measure (only if not a weight column)
            else if (hl === "uom" || hl === "unit" || hl === "unitofmeasure" || hl === "units" || hl === "measure" || hl === "uomunit") {
              candidateMap.uom = idx;
            }
            // 7. Remarks / Notes
            else if (hl.includes("remark") || hl.includes("comment") || hl.includes("note")) {
              candidateMap.remarks = idx;
            }
          });

          if (candidateMap.product !== undefined && candidateMap.size !== undefined && candidateMap.weight !== undefined) {
            headerRowIdx = r;
            headerMap = candidateMap;
            break;
          }
        }

        if (headerRowIdx === -1) {
          toast.error("Could not find required columns ('Product', 'Size', and 'Unit Weight / Wt') in the sheet header");
          return;
        }

        const dataRows = jsonData.slice(headerRowIdx + 1);
        const parsedRows = [];

        dataRows.forEach((row, i) => {
          const rawId =
            headerMap.productId !== undefined && row[headerMap.productId] !== undefined && row[headerMap.productId] !== ""
              ? Number(row[headerMap.productId])
              : undefined;
          const product = headerMap.product !== undefined && row[headerMap.product] !== undefined ? String(row[headerMap.product]).trim() : "";
          const size = headerMap.size !== undefined && row[headerMap.size] !== undefined ? String(row[headerMap.size]).trim() : "";
          
          let weightRaw = headerMap.weight !== undefined ? row[headerMap.weight] : 0;
          let weight = 0;
          if (typeof weightRaw === "number") {
            weight = weightRaw;
          } else if (weightRaw !== undefined && weightRaw !== null && weightRaw !== "") {
            const cleaned = String(weightRaw).replace(/,/g, ".").replace(/[^\d.]/g, "").trim();
            weight = parseFloat(cleaned) || 0;
          }

          const group = headerMap.group !== undefined && row[headerMap.group] !== undefined ? String(row[headerMap.group]).trim() : "";
          const uom = headerMap.uom !== undefined && row[headerMap.uom] !== undefined && String(row[headerMap.uom]).trim() !== "" ? String(row[headerMap.uom]).trim() : "Num";
          const remarks = headerMap.remarks !== undefined && row[headerMap.remarks] !== undefined ? String(row[headerMap.remarks]).trim() : "";

          if (product && size) {
            // Match with local state for product ID or fallback
            const matched = sizeWeights.find(
              (sw) =>
                (rawId && sw.productId === rawId) ||
                (sw.productName && sw.productName.trim().toLowerCase() === product.toLowerCase()) ||
                (sw.displayName && sw.displayName.trim().toLowerCase() === product.toLowerCase()) ||
                (sw.productName && sw.productName.replace(/#/g, "").trim().toLowerCase() === product.replace(/#/g, "").trim().toLowerCase())
            );

            parsedRows.push({
              rowNum: headerRowIdx + i + 2,
              productId: rawId || matched?.productId || undefined,
              productName: product,
              size,
              weight,
              group: group || matched?.group || matched?.category || "",
              uom: uom || "Num",
              remarks,
            });
          }
        });

        if (parsedRows.length === 0) {
          toast.error("No valid product and size rows found in the uploaded file");
          return;
        }

        setUploadData(parsedRows);
        setShowUploadModal(true);
      } catch (err) {
        console.error("Excel parse error:", err);
        toast.error("Failed to parse the Excel file");
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Submit Bulk Update
  const handleApplyBulkUpload = async () => {
    if (!uploadData || uploadData.length === 0) return;

    setUploadApplying(true);
    try {
      const payload = uploadData.map((row) => ({
        productId: row.productId,
        productName: row.productName,
        size: row.size,
        weight: row.weight,
        uom: row.uom || "Num",
        remarks: row.remarks || "",
      }));

      const res = await bulkUpdateProductSizeWeights(payload);
      if (res?.success || res?.successCount > 0) {
        setShowUploadModal(false);
        setUploadData([]);
        loadData();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUploadApplying(false);
    }
  };

  return (
    <div className="container-fluid py-4 px-4 bg-light min-vh-100">
      {loading && <FullPageSpinner loading={loading} />}

      {/* Header & Actions */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <Scale className="text-primary" size={28} />
            <h2 className="h4 fw-bold text-dark mb-0">Size-Wise Weight Configuration</h2>
          </div>
          <p className="text-muted small mb-0 mt-1">
            Configure unit parcel weight (in KG) per product size variant for accurate delivery charges and DTDC shipping calculations.
          </p>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <button
            onClick={handleDownloadSample}
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 shadow-sm"
            title="Download sample template"
          >
            <Download size={16} /> Sample Template
          </button>

          <button
            onClick={handleExportExcel}
            className="btn btn-outline-success btn-sm d-flex align-items-center gap-1 shadow-sm"
            title="Export to Excel"
          >
            <FileSpreadsheet size={16} /> Export Excel
          </button>

          <label className="btn btn-primary btn-sm d-flex align-items-center gap-1 shadow-sm mb-0 cursor-pointer">
            <Upload size={16} /> Bulk Upload Excel
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx, .xls, .csv"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </label>

          <button
            onClick={loadData}
            className="btn btn-light btn-sm border shadow-sm"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-sm-4">
          <div className="card border-0 shadow-sm rounded-3 p-3 bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase fw-semibold">Total Size Variants</div>
                <div className="h3 fw-bold text-dark mb-0 mt-1">{stats.total}</div>
              </div>
              <div className="bg-primary-subtle text-primary p-3 rounded-circle">
                <Layers size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-4">
          <div className="card border-0 shadow-sm rounded-3 p-3 bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase fw-semibold">Weights Configured</div>
                <div className="h3 fw-bold text-success mb-0 mt-1">{stats.configured}</div>
              </div>
              <div className="bg-success-subtle text-success p-3 rounded-circle">
                <CheckCircle size={22} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-4">
          <div className="card border-0 shadow-sm rounded-3 p-3 bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-muted small text-uppercase fw-semibold">Pending Configuration</div>
                <div className="h3 fw-bold text-warning mb-0 mt-1">{stats.missing}</div>
              </div>
              <div className="bg-warning-subtle text-warning p-3 rounded-circle">
                <AlertCircle size={22} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="card border-0 shadow-sm rounded-3 p-3 mb-4 bg-white">
        <div className="row g-3 align-items-center">
          <div className="col-12 col-md-4">
            <div className="input-group">
              <span className="input-group-text bg-light border-end-0">
                <Search size={16} className="text-muted" />
              </span>
              <input
                type="text"
                className="form-control border-start-0 bg-light"
                placeholder="Search Product or Size..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className="col-12 col-md-3">
            <select
              className="form-select bg-light"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Groups / Categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-3">
            <select
              className="form-select bg-light"
              value={weightFilter}
              onChange={(e) => {
                setWeightFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="all">All Statuses ({sizeWeights.length})</option>
              <option value="configured">Configured Only ({stats.configured})</option>
              <option value="missing">Missing Weights ({stats.missing})</option>
            </select>
          </div>

          <div className="col-12 col-md-2 text-md-end">
            <span className="text-muted small">
              Showing <strong>{filteredData.length}</strong> items
            </span>
          </div>
        </div>
      </div>

      {/* Size Weights Table */}
      <div className="card border-0 shadow-sm rounded-3 bg-white overflow-hidden mb-4">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light border-bottom">
              <tr>
                <th style={{ minWidth: "150px" }} className="py-3 px-3">Group / Category</th>
                <th style={{ minWidth: "220px" }} className="py-3 px-3">Product</th>
                <th style={{ minWidth: "90px" }} className="py-3 px-3 text-center">Size</th>
                <th style={{ minWidth: "90px" }} className="py-3 px-3 text-center">UOM</th>
                <th style={{ minWidth: "150px" }} className="py-3 px-3">Unit Weight (KG)</th>
                <th style={{ minWidth: "180px" }} className="py-3 px-3">Remarks</th>
                <th style={{ minWidth: "100px" }} className="py-3 px-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-5 text-muted">
                    No size weight configurations match your filter.
                  </td>
                </tr>
              ) : (
                paginatedData.map((item) => {
                  const key = `${item.productId}_${item.size}`;
                  const edits = editedRows[key] || {};
                  const isEdited = edits.weight !== undefined || edits.uom !== undefined || edits.remarks !== undefined;
                  const currentWeight =
                    edits.weight !== undefined
                      ? edits.weight
                      : item.weight !== undefined && item.weight !== null
                      ? item.weight
                      : "";
                  const currentUom = edits.uom !== undefined ? edits.uom : item.uom || "Num";
                  const currentRemarks = edits.remarks !== undefined ? edits.remarks : item.remarks || "";
                  const isSaving = savingId === key;

                  return (
                    <tr key={key} className={isEdited ? "table-warning bg-opacity-25" : ""}>
                      <td className="px-3">
                        <span className="badge bg-secondary-subtle text-secondary fw-semibold">
                          {item.group || item.category || "General"}
                        </span>
                      </td>
                      <td className="px-3">
                        <div className="fw-semibold text-dark">{item.productName}</div>
                        {item.displayName && item.displayName !== item.productName && (
                          <div className="small text-muted">{item.displayName}</div>
                        )}
                      </td>
                      <td className="px-3 text-center">
                        <span className="badge bg-primary-subtle text-primary fw-bold px-2 py-1">
                          {item.size}
                        </span>
                      </td>
                      <td className="px-3 text-center">
                        <input
                          type="text"
                          className="form-control form-control-sm text-center"
                          style={{ maxWidth: "80px", margin: "0 auto" }}
                          value={currentUom}
                          onChange={(e) => handleRowChange(item.productId, item.size, "uom", e.target.value)}
                        />
                      </td>
                      <td className="px-3">
                        <div className="input-group input-group-sm" style={{ maxWidth: "140px" }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="form-control form-control-sm text-end fw-semibold"
                            value={currentWeight}
                            placeholder="0.000"
                            onFocus={(e) => {
                              if (e.target.value === "0" || e.target.value === 0) {
                                e.target.select();
                              }
                            }}
                            onChange={(e) => handleRowChange(item.productId, item.size, "weight", e.target.value)}
                          />
                          <span className="input-group-text bg-light text-muted">KG</span>
                        </div>
                        {Number(currentWeight) > 0 && (
                          <small className="text-muted d-block mt-1">
                            ≈ {Math.round(Number(currentWeight) * 1000)} g
                          </small>
                        )}
                      </td>
                      <td className="px-3">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Optional remarks"
                          value={currentRemarks}
                          onChange={(e) => handleRowChange(item.productId, item.size, "remarks", e.target.value)}
                        />
                      </td>
                      <td className="px-3 text-center">
                        <button
                          onClick={() => handleSaveRow(item)}
                          disabled={isSaving}
                          className={`btn btn-sm ${isEdited ? "btn-success" : "btn-outline-primary"}`}
                          title="Save Changes"
                        >
                          {isSaving ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            <Save size={15} />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Footer */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center p-3 border-top bg-light">
            <div className="small text-muted">
              Page {currentPage} of {totalPages}
            </div>
            <div className="d-flex gap-1">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bulk Upload Preview Modal */}
      {showUploadModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content border-0 shadow-lg rounded-3">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title d-flex align-items-center gap-2">
                  <Upload size={20} /> Bulk Size Weights Upload Preview
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowUploadModal(false)}
                />
              </div>

              <div className="modal-body p-4">
                <div className="alert alert-info py-2 px-3 small mb-3">
                  Found <strong>{uploadData.length}</strong> size weight records to update/insert. Review before applying.
                </div>

                <div className="table-responsive" style={{ maxHeight: "400px" }}>
                  <table className="table table-bordered table-sm align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th style={{ width: "60px" }}>#</th>
                        <th>Group</th>
                        <th>Product Name</th>
                        <th className="text-center">Size</th>
                        <th className="text-center">UOM</th>
                        <th className="text-end">Unit Weight (KG)</th>
                        <th>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadData.map((row, idx) => (
                        <tr key={idx}>
                          <td className="text-muted">{row.rowNum}</td>
                          <td>{row.group || "-"}</td>
                          <td className="fw-semibold">{row.productName}</td>
                          <td className="text-center">
                            <span className="badge bg-primary-subtle text-primary">{row.size}</span>
                          </td>
                          <td className="text-center">{row.uom || "Num"}</td>
                          <td className="text-end fw-bold text-success">{Number(row.weight).toFixed(3)} KG</td>
                          <td className="text-muted">{row.remarks || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowUploadModal(false)}
                  disabled={uploadApplying}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={handleApplyBulkUpload}
                  disabled={uploadApplying}
                >
                  {uploadApplying ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" /> Applying...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} /> Apply Bulk Update ({uploadData.length} Rows)
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeWeightManagement;
